<template>
  <div
    class="cards-grid-item rounded-xl overflow-hidden h-full min-w-0"
    :class="isBordered && 'border'"
    :style="{
      borderColor: borderColor || '#414141',
      boxShadow: isShadowed
        ? `1px 3px 5px ${hexToRgba(shadowColor || '#414141', 0.2)}`
        : '',
    }"
  >
    <div
      v-if="isNotValid"
      class="cards-grid-item__media text-red-700 px-4 py-2"
    >
      Please, don't use links in markdown text if you have the link on the card
    </div>
    <BaseLink
      v-else-if="card.link && linkUrl"
      :href="isRelativeLink(card.link) ? undefined : linkUrl"
      :to="isRelativeLink(card.link) ? linkUrl : undefined"
      :target="getLinkTarget(card.link, card.isBlank)"
      class="cards-grid-item__content !block w-full h-full min-w-0 overflow-hidden"
      weight="normal"
    >
      <CardsTextContent
        v-if="card.cardType === 'text'"
        :card="card"
        :marked-text="markedText"
        :horizontal-padding="horizontalPadding"
        :vertical-padding="verticalPadding"
      />
      <BaseImage
        v-else-if="card.imageUrl"
        :src="card.imageUrl"
        loading="lazy"
        class="cards-grid-item__media object-cover w-full h-full"
        :style="{
          backgroundColor: card.bgColor || '',
        }"
      />
    </BaseLink>
    <template v-else>
      <CardsTextContent
        v-if="card.cardType === 'text'"
        :card="card"
        :marked-text="markedText"
        :horizontal-padding="horizontalPadding"
        :vertical-padding="verticalPadding"
      />
      <BaseImage
        v-else-if="card.imageUrl"
        loading="lazy"
        :src="card.imageUrl"
        class="cards-grid-item__media object-cover w-full h-full"
        :style="{
          backgroundColor: card.bgColor || '',
        }"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import CardsTextContent from './CardsTextContent.vue'
import { hexToRgba } from '~/utils/color'

interface CardInterface {
  cardType: 'image' | 'text'
  imageUrl?: string
  text?: string
  fontWeight?: string
  textAlign?: 'start' | 'center' | 'end'
  textColor?: string
  textFontFamily?: string
  bgColor?: string
  link?: string
  linkColor?: string
  isBlank?: boolean
  isButtonShown?: boolean
  buttonLook?: 'solid' | 'border'
  buttonColor?: string
  buttonColorHover?: string
  buttonTextColor?: string
  buttonTextFontFamily?: string
  buttonText?: string
  buttonWidthDesktop?: 'content' | 'full' | 'pixels'
  buttonWidthDesktopPx?: number
  buttonWidthMobile?: 'content' | 'full' | 'pixels'
  buttonWidthMobilePx?: number
  buttonAlignDesktop?: 'text' | 'start' | 'center' | 'end'
  buttonAlignMobile?: 'text' | 'start' | 'center' | 'end'
  // Legacy settings used as fallbacks for existing cards.
  isButtonFullWidth?: boolean
  buttonAlign?: 'text' | 'start' | 'center' | 'end'
}

interface PropsInterface {
  card: CardInterface
  isShadowed: boolean
  shadowColor: string
  isBordered: boolean
  borderColor: string
  horizontalPadding?: 'none' | 'xs' | 'sm' | 'md' | 'lg'
  verticalPadding?: 'none' | 'xs' | 'sm' | 'md' | 'lg'
}

const props = defineProps<PropsInterface>()

const { getMarkedTextWithBlankLinks } = useMarkedText()
const { getLinkTarget, getLinkUrl, isRelativeLink } = useEditorLinks()

const linkUrl = computed(() => getLinkUrl(props.card.link))

const markedText = computed(() =>
  props.card.cardType === 'text' && props.card.text
    ? getMarkedTextWithBlankLinks(props.card.text, props.card.linkColor)
    : ''
)

// cards with links that also contains links in markdown cause the hydration mismatch error
const isNotValid = computed(
  () => props.card.link && markedText.value.includes('<a')
)
</script>
